class CustomisationsHandler extends HTMLElement {

  constructor() {
    super();

    this.type = 'unified';
  }

  connectedCallback() {
    this.hasRoster = this.dataset.hasRoster === 'true';
    // Print-only: the product is the print itself (a jersey the customer owns).
    this.printOnly = this.dataset.printOnly === 'true';
    this.mode = this.printOnly ? 'custom' : null;

    // Prices come off the selected style's data attributes, set by the
    // print_style metaobject. No product JSON, no tag matching.
    this.data = {
      pricePerLetter: 0,
      pricePerDigit: 0,
      includedLetters: null,
      includedDigits: null,
      hasCharacterProducts: false,
    }
    this.customisation = {
      style: null,
      flatPrice: null,
      name: null,
      number: null,
      color: null,
      badgeName: null,
      badgePrice: 0,
      styleTitle: null,
      selectedOptionValue: null,
    }
    this.colorMemory = {}
    this.jersey = {
      price: this.printOnly ? 0 : parseInt(this.dataset.jerseyPrice, 10) || 0,
      label: this.dataset.jerseyLabel || this.dataset.productTitle || '',
    }

    this.init();
    this.bindEvents();
    this.handleURLParams();
  }

  disconnectedCallback() {
    this.variantChangeUnsubscriber?.();
  }

  elements = {
    clearButton: this.querySelector('[data-clear]'),
    jobInput: this.querySelector('[data-print-job-input]'),
    nameInput: this.querySelector('[data-name-input]'),
    numberInput: this.querySelector('[data-number-input]'),
    styleInput: this.querySelector('[data-style-prop-input]'),
    mainOptionInput: this.querySelector('[data-main-option-attribute]'),
    mainVariantInput: document.getElementById(this.dataset.formId)?.querySelector('[name=id]'),
    fontOptions: this.querySelector('[data-font-options]'),
    fontOptionsVariants: this.querySelectorAll('[data-variant-value]'),
    errorMessage: this.querySelector('[data-error-message]'),
    summaryBlock: this.querySelector('[data-summary]'),
    personaliseBody: this.querySelector('[data-personalise-body]'),
    quantityInput: this.closest('product-info')?.querySelector('quantity-input input'),
    modeToggle: this.querySelector('[data-mode-toggle]'),
    modeOptions: this.querySelectorAll('[data-mode-option]'),
    playerPanel: this.querySelector('[data-mode-panel="player"]'),
    customPanel: this.querySelector('[data-mode-panel="custom"]'),
    rosterSelect: this.querySelector('[data-roster-select]'),
    badgeSelect: this.querySelector('[data-badge-select]'),
    badgeIdInput: this.querySelector('[data-badge-id-input]'),
    badgeQuantity: this.querySelector('[data-badge-quantity]'),
    badgeJobInput: this.querySelector('[data-badge-job-input]'),
    badgeRemove: this.querySelector('[data-badge-remove]'),
    badgePreview: {
      root: this.querySelector('[data-badge-preview]'),
      image: this.querySelector('[data-badge-preview-image]'),
      label: this.querySelector('[data-badge-preview-label]'),
      price: this.querySelector('[data-badge-preview-price]'),
    },
    summary: {
      nameNumber: this.querySelector('[data-summary-name-number]'),
      printRow: this.querySelector('[data-summary-print-row]'),
      printLabel: this.querySelector('[data-summary-print-label]'),
      printPrice: this.querySelector('[data-summary-print-price]'),
      printBreakdown: this.querySelector('[data-summary-print-breakdown]'),
      printDetail: this.querySelector('[data-summary-print-detail]'),
      badgeRow: this.querySelector('[data-summary-badge-row]'),
      badgeLabel: this.querySelector('[data-summary-badge-label]'),
      badgePrice: this.querySelector('[data-summary-badge-price]'),
      jerseyLabel: this.querySelector('[data-summary-jersey-label]'),
      jerseyPrice: this.querySelector('[data-summary-jersey-price]'),
      totalPrice: this.querySelector('[data-summary-total-price]'),
    }
  }

  events = {
    handleClearClick: (e) => {
      e.preventDefault();

      this.mode = this.printOnly ? 'custom' : null;
      this.applyMode(this.mode);

      if (this.elements.rosterSelect) this.elements.rosterSelect.selectedIndex = 0;
      this.elements.nameInput.value = '';
      this.elements.numberInput.value = '';
      this.customisation.name = null;
      this.customisation.number = null;

      this.resetFontStyle();
      this.resetBadge();

      this.updateItemAttributes();
      this.updateSummary();
      this.trackPersonalisationEvent('personalise_cleared');
    },
    handleModeChange: (e) => {
      this.mode = e.target.value;
      this.applyMode(this.mode);

      // switching name source clears whatever was entered under the other one
      if (this.elements.rosterSelect) this.elements.rosterSelect.selectedIndex = 0;
      this.elements.nameInput.value = '';
      this.elements.numberInput.value = '';
      this.customisation.name = null;
      this.customisation.number = null;

      this.updateItemAttributes();
      this.updateSummary();
      this.trackPersonalisationEvent('personalise_mode_selected', { mode: this.mode });
    },
    handleRosterChange: (e) => {
      const value = e.target.value;

      if (!value) {
        this.customisation.name = null;
        this.customisation.number = null;
      } else {
        const [name, number] = value.split(',');
        this.customisation.name = (name || '').trim().toUpperCase();
        this.customisation.number = (number || '').trim().slice(0, 2);
      }

      this.elements.nameInput.value = this.customisation.name ?? '';
      this.elements.numberInput.value = this.customisation.number ?? '';

      this.updateItemAttributes();
      this.updateSummary();
      if (value) {
        this.trackPersonalisationEvent('personalise_player_selected', {
          player_print_name: this.customisation.name,
          player_number: this.customisation.number,
        });
      }
    },
    handleNameInput: (e) => {
      e.target.value = e.target.value.toUpperCase();
      this.customisation.name = e.target.value.trim(); // ensure no additional spaces are added
      this.updateItemAttributes();
      this.updateSummary();
    },
    handleNumberInput: (e) => {
      // Digits only, kept as text so 00 and 07 survive.
      const val = e.target.value.replace(/\D/g, '').slice(0, 2);
      e.target.value = val;
      this.customisation.number = val;
      this.updateItemAttributes();
      this.updateSummary();
    },
    handlePrintStyleChange: (e) => {
      // Event delegation is needed as the current target is a common ancestor for all radio buttons.
      // stop if the target is a font color-option
      if ( e.target.dataset.type === 'color-option' ) return;

      const style = e.target.value;
      const target = e.target;
      const label = target.nextElementSibling;
      const colorOptions = (target.dataset.options || '').split(',').map(c => c.trim()).filter(Boolean);
      const selects = label.querySelectorAll('select');
      const inputs = label.querySelectorAll('input:not([type=radio][name=print_style])');

      // disable all inputs
      this.disableFontOptionsInputs();

      // re-enable inputs for the selected style's label
      [...selects, ...inputs].forEach( el => {
        if (el.hasAttribute('data-locked')) return;
        el.disabled = false;
        el.ariaDisabled = 'false';
      })

      if (colorOptions.length) {
        // restore remembered colour for this style, or default to first option
        const availableColors = colorOptions.map(c => c.toLowerCase());
        const rememberedOrKitColour = this.colorMemory[style] ?? target.dataset.defaultOption?.toLowerCase();
        const selectedColor = availableColors.includes(rememberedOrKitColour) ? rememberedOrKitColour : availableColors[0];
        const colorRadios = Array.from(label.querySelectorAll('input[type=radio][data-type=color-option]'));
        const colorIndex = Math.max(0, colorRadios.findIndex(r => r.value.toLowerCase() === selectedColor));
        if (colorRadios[colorIndex]) {
          colorRadios[colorIndex].checked = true;
        }
        selects.forEach(select => select.selectedIndex = colorIndex);
        this.customisation.color = selectedColor;
        this.setSelectedOption(label, colorRadios[colorIndex]?.value);
      } else {
        // flat-price options (e.g. Custom font) have no colour choice
        this.customisation.color = null;
        this.setMainOption(null, null);
      }

      this.customisation.style = style;
      this.customisation.styleTitle = label.querySelector('.font-option__title')?.textContent.trim() ?? null;
      this.trackPersonalisationEvent('personalise_font_selected', {
        print_style: style,
        print_style_title: this.customisation.styleTitle,
        pricing_mode: target.dataset.mode,
      });

      this.customisation.flatPrice = target.dataset.mode === 'flat'
        ? parseInt(target.dataset.flatPrice, 10) || 0
        : null;
      // Print-only: the style's flat variant is the product being bought.
      if (this.printOnly && target.dataset.flatVariantId && this.elements.mainVariantInput) {
        this.elements.mainVariantInput.value = target.dataset.flatVariantId;
      }
      this.data.hasCharacterProducts = target.dataset.letterPrice !== undefined;
      this.data.pricePerLetter = parseInt(target.dataset.letterPrice, 10) || 0;
      this.data.pricePerDigit = parseInt(target.dataset.digitPrice, 10) || 0;
      this.data.includedLetters = this.parseLimit(target.dataset.includedLetters);
      this.data.includedDigits = this.parseLimit(target.dataset.includedDigits);
      this.elements.selectedFontLabel = label;

      this.elements.styleInput.value = this.printOnly ? this.customisation.styleTitle : this.customisation.style;
      this.optionName = target.dataset.optionName || 'Colour';

      this.updateItemAttributes();
      this.updateSummary();
    },
    handleFontOptionVariantClick: (e) => {
      // currentTarget is the option label; target can be its inner text <span>
      const target = e.currentTarget;
      const fontOption = target.closest('[data-font-option]');
      const options = fontOption.querySelector('input[data-type=font-option]').dataset.options.split(',');

      // if target has 1 option only, return immediately. No need to process.
      if ( options.length === 1 ) return;

      // if parent option is not selected, return immediately
      if ( !fontOption.querySelector('input').checked) return;

      // locked options (not this jersey's kit) can't be chosen
      const clickedOptionRadio = fontOption.querySelector(`#${CSS.escape(target.htmlFor)}`);
      if ( !clickedOptionRadio || clickedOptionRadio.disabled ) return;

      // select the correct letter and number option
      fontOption.querySelectorAll('select').forEach( select => {
        select.selectedIndex = target.dataset.index;
      })

      this.customisation.color = target.dataset.variantValue;
      this.colorMemory[this.customisation.style] = this.customisation.color;
      this.setSelectedOption(fontOption, target.title);
      this.updateSummary();
    },
    handleBadgeChange: (e) => {
      const value = e.target.value;
      const selected = e.target.selectedOptions[0];
      const price = value ? parseInt(selected?.dataset.badgePrice || '0', 10) : 0;

      this.elements.badgeIdInput.value = value;
      this.elements.badgeIdInput.disabled = !value;
      this.elements.badgeQuantity.disabled = !value;
      this.elements.badgeJobInput.disabled = !value;

      this.customisation.badgeName = value ? (selected.dataset.badgeLabel || selected.textContent.trim()) : null;
      this.customisation.badgePrice = price;

      this.renderBadgePreview(selected, value);
      if (value) {
        this.trackPersonalisationEvent('personalise_patch_selected', {
          patch_variant_id: value,
          patch_name: this.customisation.badgeName,
          patch_price: price,
        });
      }
      this.updateSummary();
    },
    handleBadgeRemove: (e) => {
      e.preventDefault();
      this.resetBadge();
      this.updateSummary();
    },
    handleJerseyVariantChange: ({ data }) => {
      // Print-only has no jersey line to price.
      if (this.printOnly) return;
      const productSectionId = this.closest('product-info')?.dataset.section;
      if (productSectionId && data.sectionId && data.sectionId !== productSectionId) return;
      if (!data.variant) return;

      this.jersey.price = data.variant.price;
      const productTitle = this.dataset.productTitle || '';
      this.jersey.label = data.variant.title === 'Default Title' ? productTitle : `${productTitle} · ${data.variant.title}`;
      this.updateSummary();
    },
    handleInfoToggle: (e) => {
      e.preventDefault();
      const button = e.currentTarget;
      const panel = this.querySelector(`#${button.dataset.infoToggle}`);
      if (!panel) return;

      const open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!open));
      panel.hidden = open;
    }
  }

  bindEvents() {
    this.elements.clearButton?.addEventListener('click', this.events.handleClearClick);
    this.elements.nameInput.addEventListener('input', this.events.handleNameInput);
    this.elements.numberInput.addEventListener('input', this.events.handleNumberInput);
    this.elements.fontOptions.addEventListener('change', this.events.handlePrintStyleChange);
    this.elements.fontOptionsVariants.forEach( variant => {
      variant.addEventListener('click', this.events.handleFontOptionVariantClick);
    })
    this.elements.modeOptions.forEach( option => {
      option.addEventListener('change', this.events.handleModeChange);
    });
    this.elements.rosterSelect?.addEventListener('change', this.events.handleRosterChange);
    this.elements.badgeSelect?.addEventListener('change', this.events.handleBadgeChange);
    this.elements.badgeRemove?.addEventListener('click', this.events.handleBadgeRemove);
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.events.handleJerseyVariantChange);
    this.querySelectorAll('[data-info-toggle]').forEach( button => {
      button.addEventListener('click', this.events.handleInfoToggle);
    });
  }

  init() {
    // start with no font style selected — every card's per-item inputs stay inert
    // until the customer picks one
    this.disableFontOptionsInputs();
    this.applyMode(this.mode);
    this.updateSummary();
  }

  handleURLParams() {
    const url = new URL(window.location.href);

    if ( url.searchParams.has('debug') ) {
      this.debug = true;
      this.dataset.debug = true;
    }
  }

  // mode: 'player' | 'custom' | null. null collapses the panel body, disables
  // the personalisation fields and releases the quantity lock.
  applyMode(mode) {
    const isPlayerMode = mode === 'player' && this.hasRoster;

    if (this.elements.personaliseBody) this.elements.personaliseBody.hidden = !mode;
    if (this.elements.playerPanel) this.elements.playerPanel.hidden = !isPlayerMode;
    if (this.elements.customPanel) this.elements.customPanel.hidden = isPlayerMode;

    this.elements.modeOptions.forEach( option => {
      option.checked = option.value === mode;
    });

    const personalisationFields = [
      this.elements.nameInput,
      this.elements.numberInput,
      this.elements.styleInput,
      this.elements.jobInput,
    ];
    personalisationFields.forEach( personalisationField => {
      personalisationField.disabled = !mode;
    });

    this.lockQuantity(!!mode);
  }

  // One personalisation per jersey: while personalising, the product quantity
  // is held at 1 through its max attribute, which Dawn's quantity-input reads.
  lockQuantity(shouldLock){
    const jerseyQuantityInput = this.elements.quantityInput;
    if (!jerseyQuantityInput) return;

    const isQuantityLocked = jerseyQuantityInput.dataset.personaliseLocked === 'true';
    if (shouldLock && !isQuantityLocked) {
      jerseyQuantityInput.dataset.personaliseMax = jerseyQuantityInput.getAttribute('max') ?? '';
      jerseyQuantityInput.dataset.personaliseLocked = 'true';
      jerseyQuantityInput.setAttribute('max', '1');
      jerseyQuantityInput.value = 1;
      jerseyQuantityInput.readOnly = true;
    } else if (!shouldLock && isQuantityLocked) {
      const originalMax = jerseyQuantityInput.dataset.personaliseMax;
      if (originalMax) jerseyQuantityInput.setAttribute('max', originalMax);
      else jerseyQuantityInput.removeAttribute('max');
      delete jerseyQuantityInput.dataset.personaliseMax;
      delete jerseyQuantityInput.dataset.personaliseLocked;
      jerseyQuantityInput.readOnly = false;
    } else {
      return;
    }
    jerseyQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }


  // Included-character limit from a data attribute; null when the style sets none.
  parseLimit(value) {
    const limit = parseInt(value, 10);
    return Number.isNaN(limit) ? null : limit;
  }

  // Characters printed: spaces don't count, punctuation does.
  getLetterCount() {
    return this.customisation.name ? this.customisation.name.replace(/\s+/g, '').length : 0;
  }

  getDigitCount() {
    return this.customisation.number ? `${this.customisation.number}`.length : 0;
  }

  isFlatStyle() {
    return this.customisation.flatPrice != null;
  }

  // Characters past the flat style's included limits; 0 on per-character styles
  // and on flat styles without a limit.
  getExtraLetterCount() {
    if (!this.isFlatStyle() || this.data.includedLetters == null) return 0;
    return Math.max(0, this.getLetterCount() - this.data.includedLetters);
  }

  getExtraDigitCount() {
    if (!this.isFlatStyle() || this.data.includedDigits == null) return 0;
    return Math.max(0, this.getDigitCount() - this.data.includedDigits);
  }

  // Quantities for the letter and number cart lines of the selected style.
  getLetterLineQuantity() {
    if (!this.data.hasCharacterProducts) return 0;
    return this.isFlatStyle() ? this.getExtraLetterCount() : this.getLetterCount();
  }

  getDigitLineQuantity() {
    if (!this.data.hasCharacterProducts) return 0;
    return this.isFlatStyle() ? this.getExtraDigitCount() : this.getDigitCount();
  }

  // A flat style with no letter/number products caps the name and number at
  // its included limits.
  exceedsCharacterCap() {
    if (!this.isFlatStyle() || this.data.hasCharacterProducts) return false;
    return this.getExtraLetterCount() > 0 || this.getExtraDigitCount() > 0;
  }

  getCharacterCapMessage() {
    return `${this.customisation.styleTitle} prints up to ${this.data.includedLetters} letters and ${this.data.includedDigits} numbers. Shorten the name or choose another style.`;
  }

  // Price of the print lines only (flat nameset plus extra characters, or every
  // character on per-character styles), in cents.
  getPrintPrice() {
    if (!this.customisation.style) return 0;
    if (!this.customisation.name && !this.customisation.number) return 0;

    const characterPrice = this.getLetterLineQuantity() * this.data.pricePerLetter
      + this.getDigitLineQuantity() * this.data.pricePerDigit;
    return this.isFlatStyle() ? this.customisation.flatPrice + characterPrice : characterPrice;
  }

  // Price of everything the personalisation adds to the cart (print + patch), in cents.
  getTotalPrice() {
    return this.getPrintPrice() + (this.customisation.badgePrice || 0);
  }

  updateItemAttributes(){
    this.querySelectorAll('[data-name-attribute]').forEach( input => {
      input.value = this.customisation.name?.length ? this.customisation.name.toUpperCase() : '';
    });
    this.querySelectorAll('[data-number-attribute]').forEach( input => {
      input.value = this.customisation.number;
    });
  }

  updateSummary(){
    this.syncPrintLines();
    this.renderCharacterCapError();
    this.renderSummary();
  }

  // Sets the selected style's cart line quantities and submits only the lines
  // with something to print. Unselected styles stay fully disabled.
  syncPrintLines(){
    const selectedFontLabel = this.elements.selectedFontLabel;
    if (!selectedFontLabel || !this.customisation.style) return;

    const hasPrint = Boolean(this.customisation.name || this.customisation.number);
    const lineQuantities = {
      // Print-only: the flat variant is already the main line.
      flat: hasPrint && !this.printOnly ? 1 : 0,
      letter: this.getLetterLineQuantity(),
      number: this.getDigitLineQuantity(),
    };

    selectedFontLabel.querySelectorAll('[data-print-line]').forEach( printLine => {
      const quantity = lineQuantities[printLine.dataset.printLine] ?? 0;
      const quantityInput = printLine.querySelector('input[type=number]');
      if (quantityInput) quantityInput.value = quantity;

      printLine.querySelectorAll('input,select').forEach( field => {
        field.disabled = quantity === 0;
        field.ariaDisabled = String(quantity === 0);
      });
    });
  }

  renderCharacterCapError(){
    const errorMessage = this.elements.errorMessage;
    if (this.exceedsCharacterCap()) {
      errorMessage.textContent = this.getCharacterCapMessage();
      errorMessage.dataset.characterCap = 'true';
    } else if (errorMessage.dataset.characterCap) {
      errorMessage.textContent = '';
      delete errorMessage.dataset.characterCap;
    }
  }

  // Fills the personalisation overview. Rows with nothing to show are hidden,
  // and the whole overview stays hidden until something is chosen.
  renderSummary() {
    const summary = this.elements.summary;
    const { name, number, styleTitle, selectedOptionValue, badgeName, badgePrice } = this.customisation;

    summary.jerseyLabel.textContent = this.jersey.label;
    summary.jerseyPrice.textContent = Utilities.formatPrice(this.jersey.price);

    const printPrice = this.getPrintPrice();
    const hasPrint = Boolean(styleTitle) && printPrice > 0;
    const optionName = this.optionName || 'Colour';
    const selectedOptionText = !selectedOptionValue
      ? ''
      : optionName === 'Colour' ? selectedOptionValue : `${selectedOptionValue} ${optionName.toLowerCase()}`;
    summary.printLabel.textContent = hasPrint
      ? [`${styleTitle} print`, selectedOptionText].filter(Boolean).join(' · ')
      : '';
    summary.printPrice.textContent = hasPrint ? Utilities.formatPrice(printPrice) : '';
    summary.printRow.hidden = !hasPrint;

    const nameAndNumber = [name, number].filter(Boolean).join(' ');
    summary.nameNumber.textContent = nameAndNumber;
    summary.nameNumber.hidden = !nameAndNumber;

    const breakdownParts = [];
    if (hasPrint) {
      const letterLineQuantity = this.getLetterLineQuantity();
      const digitLineQuantity = this.getDigitLineQuantity();
      const extraPrefix = this.isFlatStyle() ? 'extra ' : '';
      if (this.isFlatStyle()) breakdownParts.push(Utilities.formatPrice(this.customisation.flatPrice));
      if (letterLineQuantity) breakdownParts.push(`${letterLineQuantity} ${extraPrefix}${letterLineQuantity === 1 ? 'letter' : 'letters'} × ${Utilities.formatPrice(this.data.pricePerLetter)}`);
      if (digitLineQuantity) breakdownParts.push(`${digitLineQuantity} ${extraPrefix}${digitLineQuantity === 1 ? 'number' : 'numbers'} × ${Utilities.formatPrice(this.data.pricePerDigit)}`);
      if (breakdownParts.length === 1 && this.isFlatStyle()) breakdownParts.length = 0;
    }
    summary.printBreakdown.textContent = breakdownParts.join(' + ');
    summary.printBreakdown.hidden = breakdownParts.length === 0;
    summary.printDetail.hidden = !hasPrint || (!nameAndNumber && breakdownParts.length === 0);

    summary.badgeLabel.textContent = badgeName ? `Patch · ${badgeName}` : '';
    summary.badgePrice.textContent = badgeName ? Utilities.formatPrice(badgePrice) : '';
    summary.badgeRow.hidden = !badgeName;

    const personalisationPrice = this.getTotalPrice();
    summary.totalPrice.textContent = Utilities.formatPrice(this.jersey.price + personalisationPrice);

    if (this.elements.summaryBlock) {
      this.elements.summaryBlock.hidden = personalisationPrice === 0;
    }
  }


  renderBadgePreview(option, value){
    const preview = this.elements.badgePreview;
    if (!preview.root) return;

    if (!value) {
      preview.root.hidden = true;
      preview.image.removeAttribute('src');
      return;
    }

    const src = option.dataset.badgeImage;
    if (src) {
      preview.image.src = src;
      preview.image.alt = this.customisation.badgeName ?? '';
      preview.image.hidden = false;
    } else {
      preview.image.removeAttribute('src');
      preview.image.hidden = true;
    }

    preview.label.textContent = this.customisation.badgeName ?? '';
    preview.price.textContent = Utilities.formatPrice(this.customisation.badgePrice);
    preview.root.hidden = false;
  }

  // Shows the chosen option beside the swatches and writes it to the
  // line-item property named after the style's option_name (property present
  // on flat-price styles only).
  setSelectedOption(fontOptionElement, selectedOptionValue){
    this.customisation.selectedOptionValue = selectedOptionValue ?? null;
    this.showOptionPreview(fontOptionElement, selectedOptionValue);
    const selectedOptionLabel = fontOptionElement.querySelector('[data-option-value]');
    if (selectedOptionLabel) selectedOptionLabel.textContent = selectedOptionValue ?? '';
    fontOptionElement.querySelectorAll('[data-option-attribute]').forEach( selectedOptionPropertyInput => {
      selectedOptionPropertyInput.value = selectedOptionValue ?? '';
    });
    const styleRadio = fontOptionElement.closest('[data-font-option]')?.querySelector('input[data-type=font-option]');
    this.setMainOption(styleRadio?.dataset.optionName, selectedOptionValue);
  }

  // Print-only: writes the chosen option (e.g. Colour) onto the main line as
  // properties[<option name>]. Disabled when the style has no options.
  setMainOption(optionName, selectedOptionValue){
    const mainOptionInput = this.elements.mainOptionInput;
    if (!mainOptionInput) return;

    const hasOption = Boolean(optionName && selectedOptionValue);
    mainOptionInput.name = hasOption ? `properties[${optionName}]` : '';
    mainOptionInput.value = hasOption ? selectedOptionValue : '';
    mainOptionInput.disabled = !hasOption;
  }

  // Swaps the card image to the selected option's photo when it has one,
  // otherwise back to the style's own preview.
  showOptionPreview(fontOptionElement, selectedOptionValue){
    const previewImage = fontOptionElement.querySelector('[data-option-preview]');
    if (!previewImage) return;

    const selectedOptionLabel = selectedOptionValue
      ? fontOptionElement.querySelector(`label[data-variant-value="${CSS.escape(selectedOptionValue.toLowerCase())}"]`)
      : null;
    const optionImageSrc = selectedOptionLabel?.dataset.optionImage;
    previewImage.src = optionImageSrc || previewImage.dataset.defaultSrc;
  }

  resetOptionPreviews(){
    this.elements.fontOptions.querySelectorAll('[data-option-preview]').forEach( previewImage => {
      previewImage.src = previewImage.dataset.defaultSrc;
    });
  }

  disableFontOptionsInputs(){
    this.resetOptionPreviews();
    this.elements.fontOptions.querySelectorAll('[data-option-value]').forEach( el => {
      el.textContent = '';
    });
    this.elements.fontOptions.querySelectorAll('select,input:not([type=radio][name=print_style])').forEach( el => {
      el.disabled = true;
      el.ariaDisabled = true;
      if ( el.checked ) {
        el.checked = false;
      }
    });
  }

  resetFontStyle(){
    this.elements.fontOptions.querySelectorAll('input[type=radio][name=print_style]').forEach( el => {
      el.checked = false;
    });
    this.disableFontOptionsInputs();
    this.customisation.style = null;
    this.customisation.flatPrice = null;
    this.customisation.color = null;
    this.customisation.styleTitle = null;
    this.customisation.selectedOptionValue = null;
    this.data.pricePerLetter = 0;
    this.data.pricePerDigit = 0;
    this.data.includedLetters = null;
    this.data.includedDigits = null;
    this.data.hasCharacterProducts = false;
    this.elements.selectedFontLabel = null;
    this.elements.styleInput.value = '';
    this.setMainOption(null, null);
    this.renderCharacterCapError();
  }

  resetBadge(){
    if (!this.elements.badgeSelect) return;
    this.elements.badgeSelect.selectedIndex = 0;
    this.elements.badgeIdInput.value = '';
    this.elements.badgeIdInput.disabled = true;
    this.elements.badgeQuantity.disabled = true;
    this.elements.badgeJobInput.disabled = true;
    this.customisation.badgeName = null;
    this.customisation.badgePrice = 0;
    this.renderBadgePreview(null, '');
  }

  // Publishes a custom Shopify analytics event for Customer events / web pixels
  // (prefixed `soccerworld:`). Typed names are never sent, only their length.
  trackPersonalisationEvent(eventName, eventData = {}){
    const publishAnalyticsEvent = window.Shopify?.analytics?.publish;
    if (typeof publishAnalyticsEvent !== 'function') return;

    publishAnalyticsEvent(`soccerworld:${eventName}`, {
      product_id: this.dataset.productId,
      product_handle: this.dataset.productHandle,
      club_handle: this.dataset.clubHandle,
      ...eventData,
    });
  }

  // Called by product-form.js once validation passes and the cart request is about to be sent.
  trackPersonalisedAddToCart(){
    if (!this.hasCustomisation() && !this.customisation.badgeName) return;

    this.trackPersonalisationEvent('personalise_add_to_cart_submitted', {
      mode: this.mode,
      print_style: this.customisation.style,
      print_style_title: this.customisation.styleTitle,
      selected_option: this.customisation.selectedOptionValue,
      name_length: this.getLetterCount(),
      number_length: this.getDigitCount(),
      has_patch: Boolean(this.customisation.badgeName),
      print_price: this.getPrintPrice(),
      personalisation_price: this.getTotalPrice(),
      jersey_price: this.jersey.price,
      currency: window.Shopify?.currency?.active,
    });
  }

  hasCustomisation() {
    const { name, number, style } = this.customisation;
    return !!name || !!number || !!style;
  }

  isValid() {
    const { name, number, style } = this.customisation;

    const hasNameOrNumber = !!name || !!number;
    const hasStyle = !!style;

    if (!this.hasCustomisation()) {
      // Print-only: the print is the product, so there is nothing to buy without one.
      if (this.printOnly) {
        return {
          isValid: false,
          message: 'Choose a print style and enter a name or number.',
        };
      }
      return {
        isValid: true,
        message: 'No customisation. Proceed with product only',
      };
    }

    if (hasNameOrNumber && !hasStyle) {
      return {
        isValid: false,
        message: 'Please select a style to continue.'
      };
    }

    if (hasStyle && !hasNameOrNumber) {
      return {
        isValid: false,
        message: 'Please enter a name or number to print.'
      };
    }

    if (this.exceedsCharacterCap()) {
      return {
        isValid: false,
        message: this.getCharacterCapMessage(),
      };
    }

    return {
      isValid: true,
      message: 'All good',
    };
  }

}

customElements.define('customisation-app', CustomisationsHandler);
